import { logger } from '@magicschool/logger';
import type { Language } from '@magicschool/supabase/types';
import { getStudentUrl } from '@magicschool/utils/nextjs/url';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { ERROR_CAPTCHA_FAILED } from '@/features/auth/constants';
import { clientSupabase } from '@/util/supabase';

type FormAlert = {
  type: 'success' | 'error' | 'analytics';
  messageId?: string;
  message?: string;
  duration?: number;
  page:
    | 'SignIn:Error'
    | 'SignIn:Success'
    | 'SignUp:Error'
    | 'SignUp:StudentError'
    | 'SignUp:SSOStudentError'
    | 'SignUp:Success'
    | 'User:Deleted'
    | 'ResetPassword:Error'
    | 'ResetPassword:Success'
    | 'UpdatePassword:Error'
    | 'UpdatePassword:Success';
};

export type AuthFormStore = {
  setField: SetField<AuthFormStore>;
  loading: boolean;
  email: string;
  roleSelected: boolean;
  formAlert: FormAlert | null;

  signIn: (
    email: string,
    password: string,
    next: string,
    router: AppRouterInstance,
    locale?: Language,
    captchaToken?: string,
  ) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    locale: Language,
    router: AppRouterInstance,
    agreedToTerms: boolean,
    captchaToken?: string,
  ) => Promise<void>;
  resetPassword: (email: string, captchaToken?: string) => Promise<void>;
  updatePassword: (password: string, router: AppRouterInstance) => Promise<void>;
};

const defaultState = {
  loading: false,
  formAlert: null,
  roleSelected: false,
  email: '',
};

export const ERROR_STUDENT_ACCOUNT_ALREADY_EXISTS = 'Student Account Already Exists';
export const ERROR_UNAUTHORIZED_DOMAIN = 'ERROR_UNAUTHORIZED_DOMAIN';

const isCaptchaFailure = (e: unknown): boolean => e instanceof Error && e.message === ERROR_CAPTCHA_FAILED;
const captchaFailedAlert = (page: FormAlert['page']): FormAlert => ({ type: 'error', messageId: 'auth.captcha-failed', page });

export const createAuthFormStoreSlice = createStoreSlice(
  'AuthFormStoreData',
  structuredClone(defaultState),
  ({ getFull, set, setField }) => ({
    setField,
    signIn: async (email, password, next, router, locale, captchaToken) => {
      set({ loading: true, formAlert: null });

      try {
        await getFull().AuthStoreData.signin(email, password, locale, captchaToken);
        set({ loading: false });
        router.push(next);
      } catch (e: any) {
        if (isCaptchaFailure(e)) {
          set({ loading: false, formAlert: captchaFailedAlert('SignIn:Error') });
          return;
        }
        set({ loading: false, formAlert: { type: 'error', message: e.message, page: 'SignIn:Error' } });
      }
    },
    signUp: async (email, password, locale, router, agreedToTerms, captchaToken) => {
      set({ loading: true });
      try {
        await getFull().AuthStoreData.signup(email, password, locale, agreedToTerms, captchaToken);
        router.push('/auth/signup-success');
      } catch (e) {
        if (isCaptchaFailure(e)) {
          set({ formAlert: captchaFailedAlert('SignUp:Error') });
        } else if (e instanceof Error && e.message === ERROR_STUDENT_ACCOUNT_ALREADY_EXISTS) {
          set({ formAlert: { type: 'error', messageId: 'auth.existing-student-account', page: 'SignUp:StudentError', duration: 5000 } });
          setTimeout(() => {
            router.push(getStudentUrl());
          }, 5000); // this is to give the person a chance to read the error before forwarding
        } else if (e instanceof Error && e.message === ERROR_UNAUTHORIZED_DOMAIN) {
          set({ formAlert: { type: 'error', messageId: 'auth.unauthorized-domain', page: 'SignUp:Error' } });
        } else {
          set({ formAlert: { type: 'error', messageId: 'toast.generic-error', page: 'SignUp:Error' } });
          logger.error('Error signing up', e);
        }
      } finally {
        set({ loading: false });
      }
    },
    resetPassword: async (email, captchaToken) => {
      set({ loading: true });
      try {
        await getFull().AuthStoreData.sendPasswordResetEmail(email, captchaToken);
        set({ formAlert: { type: 'success', messageId: 'auth.password-reset-message', page: 'ResetPassword:Success' } });
      } catch (err) {
        if (isCaptchaFailure(err)) {
          set({ formAlert: captchaFailedAlert('ResetPassword:Error') });
          return;
        }
        logger.error('Error sending password reset', err);
        set({ formAlert: { type: 'error', messageId: 'toast.generic-error', page: 'ResetPassword:Error' } });
      } finally {
        set({ loading: false });
      }
    },
    updatePassword: async (password, router) => {
      set({ loading: true });
      const error = await getFull().AuthStoreData.updatePassword(password);
      if (error) {
        set({ loading: false, formAlert: { type: 'error', message: error.message, page: 'UpdatePassword:Error' } });
        return;
      }

      // Need to sign out after password reset, should revoke all sessions on all devices
      const { error: signoutError } = await clientSupabase.auth.signOut({ scope: 'global' });
      if (signoutError) {
        set({ loading: false });
        return;
      }

      router.push('/auth/signin');
      set({ loading: false, formAlert: { type: 'success', messageId: 'auth.password-changed-message', page: 'UpdatePassword:Success' } });
    },
  }),
);
